import { msg } from '@lingui/core/macro';
import { TASK_SCALE_TYPE } from '@routes/sidebar/___shared___/constants.ts';
import type { TaskScaleType } from '@routes/sidebar/___shared___/types.ts';
import {
    RATING_SCALE_TABLE_HEADER_CELL_CLASS_NAME,
    RATING_SCALE_TABLE_ID,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/task-element/ui/modals/rating-scale/constants.ts';
import {
    Cell,
    DescriptionCell,
    HeaderCell,
    HeaderRow,
    Rating,
    Row,
    Table,
    Text,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/task-element/ui/modals/rating-scale/styled.tsx';
import { translateItem } from '@utils/translations.ts';

type RatingScaleItems = typeof FIVE_POINT_SCALE | typeof TWO_POINT_SCALE | typeof TWO_POINT_CONFIDENCE_SCALE;

const SUCCESS = 'success' as const;
const DEFAULT = 'default' as const;
const ERROR = 'error' as const;

const RATING_SCALE_HEADERS = [msg`Rating`, msg`Points`, msg`Description`];

const FIVE_POINT_SCALE = [
    {
        rating: msg`Exceptional`,
        points: 5,
        type: SUCCESS,
        descriptions: [msg`Consistently exceeds the required standard`, msg`Acts as a role model / guides others`],
    },
    {
        rating: msg`Exceeds expectations`,
        points: 4,
        type: SUCCESS,
        descriptions: [msg`Performs above the required standard`, msg`Works independently and confidently`],
    },
    {
        rating: msg`Meets expectations`,
        points: 3,
        type: DEFAULT,
        descriptions: [msg`Performs to the required standard`, msg`Reliable in normal situations`],
    },
    {
        rating: msg`Needs improvement`,
        points: 2,
        type: ERROR,
        descriptions: [msg`Partially meets the required standard`, msg`Requires guidance or supervision`],
    },
    {
        rating: msg`Unsatisfactory`,
        points: 1,
        type: ERROR,
        descriptions: [msg`Does not meet the required standard`, msg`Unable to perform the task correctly`],
    },
];

const TWO_POINT_SCALE = [
    {
        rating: msg`Approved`,
        points: 1,
        type: SUCCESS,
        descriptions: [msg`Meets expected criteria as per the guidelines.`],
    },
    {
        rating: msg`Disapproved`,
        points: 0,
        type: ERROR,
        descriptions: [msg`Does not meet expected criteria as per the guidelines.`],
    },
];

const TWO_POINT_CONFIDENCE_SCALE = [
    {
        rating: msg`Confident`,
        points: 1,
        type: SUCCESS,
        descriptions: [msg`Demonstrates confidence in performing the task according to the guidelines.`],
    },
    {
        rating: msg`Not Confident`,
        points: 0,
        type: ERROR,
        descriptions: [
            msg`Does not demonstrate sufficient confidence in performing the task according to the guidelines.`,
        ],
    },
];

const renderScale = (items: RatingScaleItems) => (
    <Table id={RATING_SCALE_TABLE_ID}>
        <HeaderRow>
            {RATING_SCALE_HEADERS.map((header) => (
                <HeaderCell key={header.id} className={RATING_SCALE_TABLE_HEADER_CELL_CLASS_NAME}>
                    {translateItem(header)}
                </HeaderCell>
            ))}
        </HeaderRow>

        {items.map(({ rating, points, type, descriptions }) => (
            <Row key={points}>
                <Cell>
                    <Rating $type={type}>{translateItem(rating)}</Rating>
                </Cell>
                <Cell>
                    <Text>{points}</Text>
                </Cell>
                <DescriptionCell>
                    <ul>
                        {descriptions.map((description) => (
                            <li key={description.id}>
                                <Text>{translateItem(description)}</Text>
                            </li>
                        ))}
                    </ul>
                </DescriptionCell>
            </Row>
        ))}
    </Table>
);

export const renderFivePointScaleRating = () => renderScale(FIVE_POINT_SCALE);

export const renderTwoPointScaleRating = () => renderScale(TWO_POINT_SCALE);

export const renderTwoPointConfidenceScaleRating = () => renderScale(TWO_POINT_CONFIDENCE_SCALE);

export const RATING_SCALE_RENDERERS: Record<TaskScaleType, () => React.ReactNode> = {
    [TASK_SCALE_TYPE.TwoPointApprove]: renderTwoPointScaleRating,
    [TASK_SCALE_TYPE.TwoPointConfidence]: renderTwoPointConfidenceScaleRating,
    [TASK_SCALE_TYPE.FivePointCompetence]: renderFivePointScaleRating,
};
